import { useMemo, useRef, useState } from 'react';
import {
  DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, sortableKeyboardCoordinates,
  verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getBlock, type FieldDescriptor } from '@sytel/blocks';
import { ImageIcon, Trash2, GripVertical, Plus, Copy } from 'lucide-react';
import { usePageEditor } from './usePageEditor';
import { MediaPicker } from '@/features/media/MediaPicker';
import { RichTextEditor } from '@/features/editor/RichTextEditor';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

export function BlockInspector() {
  const { blocks, selectedBlockId, updateBlockData } = usePageEditor();

  const selected = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId),
    [blocks, selectedBlockId],
  );

  if (!selected) {
    return (
      <aside className="w-96 shrink-0 border-l border-neutral-200 bg-white p-6 text-sm text-neutral-500">
        Selecciona un bloque para editar sus campos.
      </aside>
    );
  }

  const def = getBlock(selected.type);
  if (!def) {
    return (
      <aside className="w-96 shrink-0 border-l border-neutral-200 bg-white p-6 text-sm text-red-600">
        Tipo de bloque desconocido: {selected.type}
      </aside>
    );
  }

  return (
    <aside className="w-96 shrink-0 overflow-y-auto border-l border-neutral-200 bg-white">
      <div className="border-b border-neutral-200 p-4">
        <h2 className="font-semibold">{def.meta.label}</h2>
        <p className="text-xs text-neutral-500">{def.meta.description}</p>
      </div>
      <div className="space-y-4 p-4">
        {def.fields.map((field) => (
          <FieldRenderer
            key={field.name}
            field={field}
            value={(selected.data as Record<string, unknown>)[field.name]}
            onChange={(value) => {
              updateBlockData(selected.id, {
                ...(selected.data as Record<string, unknown>),
                [field.name]: value,
              });
            }}
          />
        ))}
      </div>
    </aside>
  );
}

interface FieldRendererProps {
  field: FieldDescriptor;
  value: unknown;
  onChange: (value: unknown) => void;
}

function ImageFieldRenderer({
  label, value, onChange,
}: {
  label: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {label}
      <div className="flex items-start gap-2">
        {value ? (
          <div className="relative w-24 shrink-0 overflow-hidden rounded border border-neutral-200 bg-neutral-50">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={value} alt="" className="aspect-square w-full object-cover" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute right-1 top-1 rounded bg-white/90 p-0.5 text-red-600"
              aria-label="Quitar imagen"
            >
              <Trash2 className="size-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="grid size-24 shrink-0 place-items-center rounded border border-dashed border-neutral-300 bg-neutral-50 text-neutral-400 hover:border-neutral-500 hover:text-neutral-700"
          >
            <ImageIcon className="size-6" />
          </button>
        )}
        <div className="flex-1 space-y-1.5">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="URL externa o /assets/…"
            className="w-full rounded border border-neutral-200 px-2 py-1 text-xs outline-none focus:border-neutral-900"
          />
          <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} className="w-full">
            <ImageIcon className="size-3" /> Elegir de la biblioteca
          </Button>
        </div>
      </div>
      <MediaPicker
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(url) => onChange(url)}
        accept="image"
        title="Seleccionar imagen"
      />
    </div>
  );
}

function FieldRenderer({ field, value, onChange }: FieldRendererProps) {
  const label = (
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-neutral-700">
      {field.label}
      {field.required && <span className="ml-1 text-red-500">*</span>}
    </label>
  );

  if (field.type === 'text' || field.type === 'url' || field.type === 'email') {
    return (
      <div>
        {label}
        <input
          type={field.type === 'email' ? 'email' : 'text'}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          maxLength={field.max}
          placeholder={field.placeholder}
          className="w-full rounded border border-neutral-200 px-2 py-1 text-sm outline-none focus:border-neutral-900"
        />
      </div>
    );
  }

  if (field.type === 'richtext') {
    return (
      <div>
        {label}
        <RichTextEditor value={(value as string) ?? ''} onChange={(v) => onChange(v)} />
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div>
        {label}
        <textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          maxLength={field.max}
          rows={3}
          className="w-full rounded border border-neutral-200 px-2 py-1 text-sm outline-none focus:border-neutral-900"
        />
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div>
        {label}
        <input
          type="number"
          value={(value as number) ?? ''}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full rounded border border-neutral-200 px-2 py-1 text-sm outline-none focus:border-neutral-900"
        />
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4"
        />
        <span className="text-sm">{field.label}</span>
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        {label}
        <select
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border border-neutral-200 px-2 py-1 text-sm outline-none focus:border-neutral-900"
        >
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'image') {
    return <ImageFieldRenderer label={label} value={(value as string) ?? ''} onChange={onChange} />;
  }

  if (field.type === 'object' && field.fields) {
    const obj = (value as Record<string, unknown>) ?? {};
    return (
      <div className="rounded-md border border-neutral-100 bg-neutral-50 p-3">
        {label}
        <div className="space-y-3">
          {field.fields.map((sub) => (
            <FieldRenderer
              key={sub.name}
              field={sub}
              value={obj[sub.name]}
              onChange={(v) => onChange({ ...obj, [sub.name]: v })}
            />
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'array' && field.itemFields) {
    return (
      <SortableArrayField
        label={label}
        value={value}
        onChange={onChange}
        itemFields={field.itemFields}
        max={field.max}
      />
    );
  }

  return (
    <div>
      {label}
      <input
        type="text"
        value={typeof value === 'string' ? value : JSON.stringify(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-neutral-200 px-2 py-1 text-sm outline-none focus:border-neutral-900"
      />
    </div>
  );
}

// ---------- Sortable Array Field ----------

interface SortableArrayFieldProps {
  label: React.ReactNode;
  value: unknown;
  onChange: (value: unknown) => void;
  itemFields: FieldDescriptor[];
  max?: number;
}

type Item = Record<string, unknown>;

function makeKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `k-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/**
 * Maintain stable keys per array index without persisting them in the value.
 * The keys live in a ref keyed by item identity (best-effort), so reorders feel smooth
 * and React/DnD remain happy across edits.
 */
function useStableKeys(length: number): string[] {
  const keysRef = useRef<string[]>([]);
  if (keysRef.current.length < length) {
    while (keysRef.current.length < length) keysRef.current.push(makeKey());
  } else if (keysRef.current.length > length) {
    keysRef.current = keysRef.current.slice(0, length);
  }
  return keysRef.current;
}

function SortableArrayField({ label, value, onChange, itemFields, max }: SortableArrayFieldProps) {
  const arr: Item[] = Array.isArray(value) ? (value as Item[]) : [];
  const keys = useStableKeys(arr.length);
  const keysRef = useRef(keys);
  keysRef.current = keys;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = keysRef.current.indexOf(String(active.id));
    const to = keysRef.current.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    keysRef.current = arrayMove(keysRef.current, from, to);
    onChange(arrayMove(arr, from, to));
  }

  function updateItem(idx: number, updater: (item: Item) => Item) {
    const next = arr.slice();
    const current = next[idx] ?? {};
    next[idx] = updater(current);
    onChange(next);
  }
  function removeItem(idx: number) {
    const next = arr.slice();
    next.splice(idx, 1);
    keysRef.current = keysRef.current.slice();
    keysRef.current.splice(idx, 1);
    onChange(next);
  }
  function duplicateItem(idx: number) {
    const original = arr[idx];
    if (!original) return;
    const copy = structuredClone(original);
    const next = arr.slice();
    next.splice(idx + 1, 0, copy);
    keysRef.current = keysRef.current.slice();
    keysRef.current.splice(idx + 1, 0, makeKey());
    onChange(next);
  }
  function addItem() {
    keysRef.current = [...keysRef.current, makeKey()];
    onChange([...arr, {}]);
  }

  return (
    <div>
      {label}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={keys} strategy={verticalListSortingStrategy}>
          <ul className="space-y-2">
            {arr.map((item, idx) => (
              <SortableArrayItem
                key={keys[idx]}
                id={keys[idx]!}
                index={idx}
                itemFields={itemFields}
                value={item}
                onChange={(updater) => updateItem(idx, updater)}
                onRemove={() => removeItem(idx)}
                onDuplicate={() => duplicateItem(idx)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {arr.length === 0 && (
        <div className="rounded border border-dashed border-neutral-200 px-3 py-4 text-center text-xs text-neutral-400">
          Sin elementos
        </div>
      )}
      {(!max || arr.length < max) && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addItem}
          className="mt-2 w-full"
        >
          <Plus className="size-3" /> Añadir elemento
        </Button>
      )}
    </div>
  );
}

function SortableArrayItem({
  id, index, itemFields, value, onChange, onRemove, onDuplicate,
}: {
  id: string;
  index: number;
  itemFields: FieldDescriptor[];
  value: Item;
  onChange: (updater: (item: Item) => Item) => void;
  onRemove: () => void;
  onDuplicate: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [collapsed, setCollapsed] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const preview = previewItem(value, itemFields);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-md border bg-white',
        isDragging ? 'border-neutral-400 shadow-lg' : 'border-neutral-200',
      )}
    >
      <div className="flex items-center gap-1 border-b border-neutral-100 px-1.5 py-1.5">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 active:cursor-grabbing"
          aria-label="Arrastrar"
        >
          <GripVertical className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex-1 truncate text-left text-xs text-neutral-700 hover:text-neutral-900"
        >
          <span className="mr-1.5 text-neutral-400">#{index + 1}</span>
          {preview || <span className="text-neutral-400">(vacío)</span>}
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          aria-label="Duplicar"
          title="Duplicar"
          className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <Copy className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => { if (confirm('¿Eliminar este elemento?')) onRemove(); }}
          aria-label="Eliminar"
          title="Eliminar"
          className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
      {!collapsed && (
        <div className="space-y-3 p-3">
          {itemFields.map((sub) => (
            <FieldRenderer
              key={sub.name}
              field={sub}
              value={value[sub.name]}
              onChange={(v) => onChange((item) => ({ ...item, [sub.name]: v }))}
            />
          ))}
        </div>
      )}
    </li>
  );
}

function previewItem(item: Item, itemFields: FieldDescriptor[]): string {
  const candidates = ['title', 'label', 'name', 'h3', 'question', 'text'];
  for (const key of candidates) {
    const v = item[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  for (const f of itemFields) {
    if (f.type === 'text' || f.type === 'textarea' || f.type === 'url') {
      const v = item[f.name];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  return '';
}
